import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Quote, Sparkles, Award, Instagram, Linkedin, ArrowRight, TrendingUp, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { siteMeta } from "../data/mock";
import AnimatedCounter from "./ui/AnimatedCounter";

const PawanSpotlight = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.05, 1, 1.05]);

  const [spot, setSpot] = useState({ x: 50, y: 40 });

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setSpot({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100
    });
  };

  return (
    <section
      id="pawan-spotlight"
      ref={ref}
      onMouseMove={onMove}
      className="relative overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #0a0908 0%, #110e0c 50%, #0a0908 100%)"
      }}
    >
      {/* Mouse light spot */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(500px 400px at ${spot.x}% ${spot.y}%, rgba(228,199,122,0.06), transparent 60%)`,
          transition: "background 0.2s ease-out"
        }}
      />

      <div className="container-xl py-24 md:py-32">
        <div className="flex items-center gap-4 mb-10">
          <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">04</span>
          <span className="w-16 h-px bg-[var(--rule)]" />
          <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)]">
            PAWAN · IN HIS OWN WORDS
          </span>
        </div>

        {/* Grids with layout inversion: Left is Content, Right is Image */}
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-20 items-center">
          
          {/* Left: Content */}
          <div className="flex flex-col h-full justify-between">
            <div>
              <Quote className="text-[var(--gold)] opacity-30" size={54} strokeWidth={1} />
              <motion.blockquote
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
                className="mt-6 font-display italic text-[clamp(28px,3.8vw,56px)] leading-[1.1] text-[var(--cream)] balance"
              >
                “A brand should feel like a <span className="gold-text not-italic">motion picture</span> narrative. Not just how it looks, but how it connects.”
              </motion.blockquote>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.2 }}
                className="mt-10 space-y-6 text-[16px] leading-relaxed text-[var(--cream-muted)] max-w-xl font-light"
              >
                <p>
                  <span className="text-[var(--cream)] font-medium">Pawan Katyal</span> is the Co-Founder &amp; Business Head of Meraki Solutions — a creative studio where branding, storytelling, strategy, marketing, communication, psychology &amp; consulting intersect.
                </p>
                <p>
                  With a background spanning over 25 years in management, media, marketing, sales, operations, production &amp; digital solutions, he approaches every brand &amp; business like a motion picture narrative — shaping not just how it is built, how it looks, but how it feels, speaks, connects, and stays remembered.
                </p>
                <p>
                  From creative storytelling and visual identity systems to brand &amp; business management, social media strategy, films, and experiential campaigns, his work blends intuition with intelligence, brand structure and aesthetics with business clarity &amp; conversion.
                </p>
              </motion.div>

              {/* Highlight row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.35 }}
                className="mt-12 grid grid-cols-2 gap-8 max-w-lg"
              >
                <Pillar icon={<Briefcase size={18} />} k="Experience" v="25+ Years in Media Production &amp; Sales Management" />
                <Pillar icon={<TrendingUp size={18} />} k="Approach" v="Merging Cinematic Branding with Business Growth" />
              </motion.div>
            </div>

            <div className="mt-10">
              {/* Socials & CTA Button Row */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.45 }}
                className="pt-8 border-t border-[rgba(201,169,97,0.15)] flex flex-col md:flex-row items-center justify-between gap-8"
              >
                {/* Social Icons (Left Side) */}
                <div className="flex gap-4">
                  <SocialCircle href="https://www.linkedin.com" label="LinkedIn">
                    <Linkedin size={16} />
                  </SocialCircle>
                  <SocialCircle href="https://www.instagram.com" label="Instagram">
                    <Instagram size={16} />
                  </SocialCircle>
                </div>

                {/* Explore Button (Right Side) */}
                <Link
                  to="/pawan"
                  className="btn-primary group"
                  data-cursor="explore"
                >
                  <span className="font-impact tracking-[0.15em]">
                    EXPLORE THE JOURNEY
                  </span>
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-500 group-hover:translate-x-1"
                  />
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Right: AI-styled portrait */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div
              className="relative rounded-sm overflow-hidden"
              style={{
                aspectRatio: "4 / 5",
                boxShadow:
                  "0 40px 80px rgba(0,0,0,0.6), 0 0 60px rgba(228,199,122,0.08)"
              }}
            >
              {/* Base image with duotone + ken burns */}
              <motion.div
                style={{ y, scale }}
                className="absolute inset-0"
              >
                <div
                  className="w-full h-full"
                  style={{
                    backgroundImage:
                      "url(/pawan.png)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter:
                      "grayscale(0.4) contrast(1.15) brightness(0.8) sepia(0.15)"
                  }}
                />

                {/* Gold duotone wash */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(184,115,51,0.15) 0%, transparent 35%, rgba(10,9,8,0.7) 100%)",
                    mixBlendMode: "color-burn"
                  }}
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(145deg, rgba(228,199,122,0.12) 0%, transparent 30%, rgba(10,9,8,0.55) 100%)"
                  }}
                />
              </motion.div>

              {/* Corner ticks */}
              <div
                className="absolute top-4 left-4 w-5 h-5"
                style={{
                  borderTop: "1px solid #e4c77a",
                  borderLeft: "1px solid #e4c77a"
                }}
              />
              <div
                className="absolute top-4 right-4 w-5 h-5"
                style={{
                  borderTop: "1px solid #e4c77a",
                  borderRight: "1px solid #e4c77a"
                }}
              />
              <div
                className="absolute bottom-4 left-4 w-5 h-5"
                style={{
                  borderBottom: "1px solid #e4c77a",
                  borderLeft: "1px solid #e4c77a"
                }}
              />
              <div
                className="absolute bottom-4 right-4 w-5 h-5"
                style={{
                  borderBottom: "1px solid #e4c77a",
                  borderRight: "1px solid #e4c77a"
                }}
              />

              {/* Top chip */}
              <div className="absolute top-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
                <span
                  className="font-impact text-[9px] tracking-[0.3em] px-2.5 py-1 rounded-full"
                  style={{
                    background: "rgba(10,9,8,0.6)",
                    border: "1px solid rgba(228,199,122,0.5)",
                    color: "#e4c77a",
                    backdropFilter: "blur(6px)"
                  }}
                >
                  · BUSINESS STRATEGIST
                </span>
              </div>

              {/* Name badge */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-display italic text-[28px] leading-none text-[var(--cream)]">
                  Pawan Katyal
                </div>
                <div className="mt-2 font-impact text-[13px] tracking-[0.3em] text-[var(--gold)] font-medium">
                  CO-FOUNDER &amp; BUSINESS HEAD
                </div>
              </div>
            </div>

            {/* Floating sparkle stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="absolute -bottom-6 -left-6 px-5 py-4 rounded-sm hidden md:block"
              style={{
                background: "linear-gradient(135deg, #1c1815, #0a0908)",
                border: "1px solid rgba(228,199,122,0.4)",
                boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
                zIndex: 12
              }}
            >
              <div className="flex items-center gap-2 text-[var(--gold)]">
                <Sparkles size={14} />
                <span className="font-impact text-[12px] tracking-[0.3em] font-medium">ON FIELD SINCE</span>
              </div>
              <div className="font-display text-3xl text-[var(--cream)] leading-none mt-2">
                <AnimatedCounter value={25} suffix="+" />
              </div>
              <div className="mt-1 text-[10px] text-[var(--muted)] uppercase tracking-[0.2em]">
                Years of Management &amp; Consulting
              </div>
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

const Pillar = ({ icon, k, v }) => (
  <div className="flex items-start gap-3">
    <span
      className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-[var(--gold)]"
      style={{ border: "1px solid rgba(201,169,97,0.4)" }}
    >
      {icon}
    </span>
    <div>
      <div className="font-impact text-[12px] tracking-[0.3em] text-[var(--gold)] font-medium mb-1">
        {k.toUpperCase()}
      </div>
      <div className="text-[13px] text-[var(--cream)] leading-snug">{v}</div>
    </div>
  </div>
);

const SocialCircle = ({ href, label, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    aria-label={label}
    className="btn-icon"
    data-cursor="follow"
  >
    {children}
  </a>
);

export default PawanSpotlight;
